@if (!mixedMode()) {
<ai-headlines class="block mb-4"></ai-headlines>
<div class="mb-6">
  <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
    <div class="flex-1">
      <h1 class="text-2xl font-medium text-[#141437] dark:text-white backdrop-blur-sm">Overdue Cron Payments</h1>
      <p class="text-slate-600 dark:text-slate-400 text-sm backdrop-blur-sm">Comprehensive analysis of overdue cron
        weekly attempted
        payments and trends</p>
    </div>
    <div class="flex items-center gap-3 flex-shrink-0">
      <!-- Ask Prism AI entry point (AI Headlines feature). -->
      <button (click)="openAiAssistant()"
        class="gradient-border text-[#141437] dark:text-white text-sm lg:text-xs font-medium px-5 lg:px-3 py-2.5 lg:py-3 rounded-lg transition-all duration-200 shadow-[0px_6px_12px_-6px_#AD46FF] hover:shadow-[0px_7px_12px_-5px_#AD46FF] flex items-center gap-2 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed">
        <app-icon-v2 name="sparkles" [size]="16" />
        Ask Prism AI
      </button>
      <!-- Customize Layout entry point — opens the inline popup that
           lets the user reorder, resize (4/6/8/12 width), and toggle
           visibility for every customizable section on this page. -->
      <button type="button" (click)="openCustomizeLayout()"
        class="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium hover:border-[#AD46FF]/60 hover:text-[#AD46FF] transition-all duration-200 shadow-sm whitespace-nowrap">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect width="7" height="7" x="3" y="3" rx="1" />
          <rect width="7" height="7" x="14" y="3" rx="1" />
          <rect width="7" height="7" x="14" y="14" rx="1" />
          <rect width="7" height="7" x="3" y="14" rx="1" />
        </svg>
        Customize layout
      </button>
    </div>
  </div>
</div>
}

<!-- ─────────────────────────────────────────────────────────────────────────
     OVERVIEW Customizable Grid
     Customizable 12-col grid wrapping the "Most Recent Payment Attempt"
     hero card. Renders with the persisted col-span / order / visibility
     from `layoutMap`. Default preserves the original full-width layout.
─────────────────────────────────────────────────────────────────────────── -->
<div [class]="mixedMode() ? 'unified-tile-body h-full' : ''">
  <div class="min-w-0" [ngClass]="{'h-full': mixedMode()}">
    @if (overviewSections().length > 0) {
    <div class="grid grid-cols-12 gap-4 md:gap-6 grid-flow-row-dense items-stretch mb-12" [class.mb-0]="mixedMode()">
      @for (sec of overviewSections(); track trackSection($index, sec)) {
      <div [ngClass]="getSectionColClass(sec)" class="group relative">

        <!-- Pin to Custom Dashboard quick-action -->
        @if (!mixedMode() && canPinToCustomDashboard(sec)) {
        <button type="button" (click)="addToCustomDashboard(sec); $event.stopPropagation()"
          [disabled]="isAddingToCustomDashboard(sec)"
          class="group/pin absolute cursor-pointer top-2 right-2 z-30 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border shadow-sm text-[11px] font-medium whitespace-nowrap opacity-0 translate-y-[-2px] group-hover:opacity-100 group-hover:translate-y-0 focus-visible:opacity-100 focus-visible:translate-y-0 transition-all duration-200 disabled:cursor-default"
          [ngClass]="isAddedToCustomDashboard(sec)
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-rose-900/30 dark:hover:text-rose-300 dark:hover:border-rose-800 !opacity-100 !translate-y-0'
        : 'bg-white/95 text-[#AD46FF] border-[#AD46FF]/30 hover:bg-violet-50 hover:border-[#AD46FF]/60 hover:shadow-md dark:bg-slate-800/95 dark:border-purple-500/40 dark:hover:bg-white/5'"
          [attr.aria-label]="isAddedToCustomDashboard(sec) ? 'Remove from My Custom Dashboard' : 'Add to My Custom Dashboard'"
          [title]="isAddedToCustomDashboard(sec) ? 'Click to remove from My Custom Dashboard' : 'Add to My Custom Dashboard'">
          @if (isAddingToCustomDashboard(sec)) {
          <svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <span>{{ isAddedToCustomDashboard(sec) ? 'Removing' : 'Adding' }}&hellip;</span>
          } @else if (isAddedToCustomDashboard(sec)) {
          <svg class="shrink-0 block group-hover/pin:hidden" xmlns="http://www.w3.org/2000/svg" width="11" height="11"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
          <svg class="shrink-0 hidden group-hover/pin:block" xmlns="http://www.w3.org/2000/svg" width="11" height="11"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M5 12h14" />
          </svg>
          <span
            class="inline-block max-w-0 -ml-1.5 group-hover/pin:max-w-[220px] group-hover/pin:ml-0 focus-visible:max-w-[220px] focus-visible:ml-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-out">Remove
            from Custom Dashboard</span>
          } @else {
          <svg class="shrink-0" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          <span
            class="inline-block max-w-0 -ml-1.5 group-hover/pin:max-w-[200px] group-hover/pin:ml-0 focus-visible:max-w-[200px] focus-visible:ml-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-out">Pin
            to Custom Dashboard</span>
          }
        </button>
        }

        @switch (sec.id) {

        @case (SectionId.RECENT_ATTEMPT_OVERVIEW) {
        <div
          class="relative bg-gradient-to-br from-slate-900 to-slate-800 backdrop-blur-sm dark:backdrop-blur-none rounded-xl shadow-lg border border-slate-700 p-8 text-white overflow-hidden h-full">

          <!-- Loader overlay for the card -->
          @if(mostRecentStats.loading) {
          <div class="absolute inset-0 z-10 flex items-center justify-center">
            <app-individual-toaster [loaderType]="'dark'"></app-individual-toaster>
          </div>
          }

          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center gap-3">
              <div class="bg-purple-500/80 p-2.5 rounded-lg text-white"><app-icon name="calendar"></app-icon></div>
              <div>
                <h2 class="text-white text-lg">Most Recent Payment Attempt</h2>
                <p class="text-slate-300">{{ (mostRecentStats.date | date:'EEEE, MMMM d, yyyy') || 'Loading...' }}</p>
              </div>
            </div>
            <div class="flex items-start justify-between gap-3">
              <div class="text-right">
                <p class="text-slate-300">Total Attempts</p>
                <p class="text-white">{{ mostRecentStats.data.total_attempts }}</p>
              </div>

              <div class="mt-1" (click)="mostRecentTotal()">
                <app-icon name="refreshCcw"
                  [class]="'text-purple-500 hover:text-blue-600 cursor-pointer ' + (mostRecentStats.loading ? 'animate-spin' : '')"
                  [size]="18"></app-icon>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-green-400">
                  <app-icon name="circleCheckBig" [size]="16"></app-icon>
                </span>
                <span class="text-green-400">Successful</span>
              </div>
              <p class="text-white mb-1 text-sm">{{ mostRecentStats.data.successful.total }} payments</p>
              <p class="text-green-400 mb-2">{{ mostRecentStats.data.successful.amount | currency }}</p>
              <div class="flex items-center gap-1 text-green-400">
                <app-icon name="trendingUp" [size]="14"></app-icon>
                <p class="text-sm">{{ mostRecentStats.data.successful.rate }}% success rate</p>
              </div>
            </div>
            <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-red-400">
                  <app-icon name="circleX" [size]="16"></app-icon>
                </span>
                <span class="text-red-400">Failed</span>
              </div>
              <p class="text-white mb-1 text-sm">{{ mostRecentStats.data.failed.total }} payments</p>
              <p class="text-red-400 mb-2">{{ mostRecentStats.data.failed.amount | currency }}</p>
              <div class="flex items-center gap-1 text-red-400">
                <app-icon name="trendingDown" [size]="14"></app-icon>
                <p class="text-sm">{{ mostRecentStats.data.failed.rate }}% failure rate</p>
              </div>
            </div>
            <div class="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-amber-400">
                  <app-icon name="clock" [size]="16"></app-icon>
                </span>
                <span class="text-amber-400">Pending</span>
              </div>
              <p class="text-white mb-1 text-sm">{{ mostRecentStats.data.pending.total }} payments</p>
              <p class="text-amber-400 mb-2">{{ mostRecentStats.data.pending.amount | currency }}</p>
              <div class="flex items-center gap-1 text-amber-400">
                <app-icon name="clock" [class]="'text-amber-400'" [size]="14"></app-icon>
                <p class="text-sm">Awaiting Processing</p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-3">
                <app-icon name="creditCard" [class]="'text-blue-400'" [size]="16"></app-icon>
                <span class="text-slate-200">Card Payments</span>
              </div>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-slate-400">Total</span>
                  <span class="text-white">{{ mostRecentStats.data.card_payments.total }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-green-400">Successful</span>
                  <span class="text-white">{{ mostRecentStats.data.card_payments.successful.total }} ({{
                    mostRecentStats.data.card_payments.successful.amount | currency }})</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-red-400">Failed</span>
                  <span class="text-white">{{ mostRecentStats.data.card_payments.failed.total }} ({{
                    mostRecentStats.data.card_payments.failed.amount | currency }})</span>
                </div>
              </div>
            </div>

            <div class="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-3">
                <app-icon name="wallet" [class]="'text-purple-400'" [size]="16"></app-icon>
                <span class="text-slate-200">ACH Payments</span>
              </div>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-slate-400">Total</span>
                  <span class="text-white">{{ mostRecentStats.data.ach_payments.total }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-green-400">Successful</span>
                  <span class="text-white">{{ mostRecentStats.data.ach_payments.successful.total }} ({{
                    mostRecentStats.data.ach_payments.successful.amount | currency }})</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-red-400">Failed</span>
                  <span class="text-white">{{ mostRecentStats.data.ach_payments.failed.total }} ({{
                    mostRecentStats.data.ach_payments.failed.amount
                    | currency }})</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-amber-400">Pending</span>
                  <span class="text-white">{{ mostRecentStats.data.ach_payments.pending.total }} ({{
                    mostRecentStats.data.ach_payments.pending.amount | currency }})</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        }

        }
      </div>
      }
    </div>
    } <!-- end @if overviewSections().length > 0 -->

    <!-- Chart Time Unit Selector -->
    @if (!mixedMode()) {
    <div class="flex items-center justify-between mb-8">
      <div>
        <h3 class="text-xl font-medium text-[#141437] dark:text-white backdrop-blur-sm">Payment Analytics</h3>
      </div>

      <!-- Refresh Button, Date Picker and Unit Indicator -->
      <div class="flex items-center gap-3">
        <app-date-picker [showComparison]="false" [showGroupBy]="true"
          [value]="{ startDate: selectedStartDate(), endDate: selectedEndDate(), unit: chartUnit(), comparisonType: 'none' }"
          [allowedGroupByUnits]="['week','month','quarter','year']" [alwaysAvailableUnit]="alwaysAvailableUnits()"
          (dateRangeChange)="onDateRangeChange($event)">
        </app-date-picker>

        <button type="button" (click)="refreshCustomRangeData()" [disabled]="customRangeStats.loading"
          class="flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-300 group bg-white dark:bg-surface-1 text-[#141437] dark:text-white hover:border-purple-400 dark:hover:border-line-strong hover:bg-white dark:hover:bg-surface-1/50 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 dark:focus:border-purple-400 gap-2 border border-purple-500/20 backdrop-blur-sm"
          aria-label="Refresh Data">
          <app-icon name="refreshCcw" [size]="16" [class]="'text-slate-600 dark:text-slate-300'"></app-icon>
          <span class="text-sm font-medium text-slate-700 dark:text-slate-200">Refresh</span>
        </button>
      </div>
    </div>
    }

    <!-- ─────────────────────────────────────────────────────────────────────────
     ANALYTICS Customizable Grid
     Customizable 12-col grid wrapping every date-range driven section
     (metric cards, trend charts, pie charts, stats table). Defaults
     reproduce the original visual layout.
─────────────────────────────────────────────────────────────────────────── -->
    <div class="grid grid-cols-12 gap-4 md:gap-6 grid-flow-row-dense mt-6">
      @for (sec of analyticsSections(); track trackSection($index, sec)) {
      <div [ngClass]="getSectionColClass(sec)" class="group relative">

        @if (!mixedMode() && canPinToCustomDashboard(sec)) {
        <button type="button" (click)="addToCustomDashboard(sec); $event.stopPropagation()"
          [disabled]="isAddingToCustomDashboard(sec)"
          class="group/pin absolute cursor-pointer top-2 right-2 z-20 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border shadow-sm text-[11px] font-medium whitespace-nowrap opacity-0 translate-y-[-2px] group-hover:opacity-100 group-hover:translate-y-0 focus-visible:opacity-100 focus-visible:translate-y-0 transition-all duration-200 disabled:cursor-default"
          [ngClass]="isAddedToCustomDashboard(sec)
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-rose-900/30 dark:hover:text-rose-300 dark:hover:border-rose-800 !opacity-100 !translate-y-0'
        : 'bg-white/95 text-[#AD46FF] border-[#AD46FF]/30 hover:bg-violet-50 hover:border-[#AD46FF]/60 hover:shadow-md dark:bg-slate-800/95 dark:border-purple-500/40 dark:hover:bg-white/5'"
          [attr.aria-label]="isAddedToCustomDashboard(sec) ? 'Remove from My Custom Dashboard' : 'Add to My Custom Dashboard'"
          [title]="isAddedToCustomDashboard(sec) ? 'Click to remove from My Custom Dashboard' : 'Add to My Custom Dashboard'">
          @if (isAddingToCustomDashboard(sec)) {
          <svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <span>{{ isAddedToCustomDashboard(sec) ? 'Removing' : 'Adding' }}&hellip;</span>
          } @else if (isAddedToCustomDashboard(sec)) {
          <svg class="shrink-0 block group-hover/pin:hidden" xmlns="http://www.w3.org/2000/svg" width="11" height="11"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
          <svg class="shrink-0 hidden group-hover/pin:block" xmlns="http://www.w3.org/2000/svg" width="11" height="11"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M5 12h14" />
          </svg>
          <span
            class="inline-block max-w-0 -ml-1.5 group-hover/pin:max-w-[220px] group-hover/pin:ml-0 focus-visible:max-w-[220px] focus-visible:ml-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-out">Remove
            from Custom Dashboard</span>
          } @else {
          <svg class="shrink-0" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          <span
            class="inline-block max-w-0 -ml-1.5 group-hover/pin:max-w-[200px] group-hover/pin:ml-0 focus-visible:max-w-[200px] focus-visible:ml-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-out">Pin
            to Custom Dashboard</span>
          }
        </button>
        }

        @switch (sec.id) {

        @case (SectionId.ANALYTICS_SUMMARY) {
        <div
          class="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 border border-slate-200 dark:border-slate-700 p-6 bg-white/90 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl">
          @if(customRangeStats.loading) {
          <div class="absolute inset-0 z-10 flex items-center justify-center">
            <app-individual-toaster [loaderType]="'dark'"></app-individual-toaster>
          </div>
          }
          <div
            class="bg-white dark:bg-surface-1 backdrop-blur-md rounded-xl shadow-sm border border-blue-200 dark:border-blue-200/30 p-6 hover:shadow-md transition-shadow">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <p class="text-slate-600 dark:text-slate-400 text-sm mb-2">Total Payments</p>
                <p class="text-slate-900 dark:text-white text-sm mb-1">{{ customRangeStats.data.stat1.total_payments |
                  number }}</p>
                <p class="text-slate-500 dark:text-slate-400 text-sm">{{ customRangeStats.dataPoints }} data points</p>
              </div>
              <div class="bg-blue-50 dark:bg-blue-50/20 text-blue-500 p-2 rounded-lg">
                <app-icon name="chartLine" [size]="24"></app-icon>
              </div>
            </div>
          </div>
          <div
            class="bg-white dark:bg-surface-1 backdrop-blur-md rounded-xl shadow-sm border border-green-200 dark:border-green-200/30 p-6 hover:shadow-md transition-shadow">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <p class="text-slate-600 dark:text-slate-400 text-sm mb-2">Success Rate</p>
                <p class="text-slate-900 dark:text-white text-sm mb-1">{{ customRangeStats.data.stat1.successRate }}%
                </p>
                <p class="text-slate-500 dark:text-slate-400 text-sm">{{ customRangeStats.data.stat1.successPayments |
                  number }} successful</p>
              </div>
              <div class="bg-green-50 dark:bg-green-50/20 text-green-500 p-2 rounded-lg">
                <app-icon name="trendingUp" [size]="24"></app-icon>
              </div>
            </div>
          </div>
          <div
            class="bg-white dark:bg-surface-1 backdrop-blur-md rounded-xl shadow-sm border border-red-200 dark:border-red-200/30 p-6 hover:shadow-md transition-shadow">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <p class="text-slate-600 dark:text-slate-400 text-sm mb-2">Failure Rate</p>
                <p class="text-slate-900 dark:text-white text-sm mb-1">{{ customRangeStats.data.stat1.failedRate }}%</p>
                <p class="text-slate-500 dark:text-slate-400 text-sm">{{ customRangeStats.data.stat1.failedPayments |
                  number }} failed</p>
              </div>
              <div class="bg-red-50 dark:bg-red-50/20 text-red-500 p-2 rounded-lg">
                <app-icon name="trendingDown" [size]="24"></app-icon>
              </div>
            </div>
          </div>
          <div
            class="bg-white dark:bg-surface-1 backdrop-blur-md rounded-xl shadow-sm border border-indigo-200 dark:border-indigo-200/30 p-6 hover:shadow-md transition-shadow">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <p class="text-slate-600 dark:text-slate-400 text-sm mb-2">Total Revenue</p>
                <p class="text-slate-900 dark:text-white text-sm mb-1">{{ customRangeStats.data.stat1.totalRevenue |
                  currency }}</p>
                <p class="text-slate-500 dark:text-slate-400 text-sm">Successful Transactions</p>
              </div>
              <div class="bg-indigo-50 dark:bg-indigo-50/20 text-indigo-500 p-2 rounded-lg">
                <app-icon name="dollarSign" [size]="24"></app-icon>
              </div>
            </div>
          </div>
        </div>
        }

        @case (SectionId.VOLUME_TRENDS) {
        <div class="relative w-full h-[380px]">
          @if(customRangeStats.loading) {
          <div
            class="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl">
            <app-individual-toaster [loaderType]="'dark'"></app-individual-toaster>
          </div>
          }
          @if(paymentVolumeSeries()[0].data.length === 0) {
          <div
            class="w-full h-full bg-white dark:bg-surface-1 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col">
            <h3 class="text-slate-700 dark:text-white font-medium mb-6">Payment Volume Trends</h3>
            <div class="flex-1 flex items-center justify-center text-slate-500 dark:text-slate-400">
              <p class="text-sm">No data available</p>
            </div>
          </div>
          } @else {
          <app-line-area-chart [series]="paymentVolumeSeries()" [title]="'Payment Volume Trends'"
            [titleClass]="'text-slate-700 dark:text-white'" [showCard]="true" [cardClass]="'card-bg'"
            [curveType]="'monotoneX'" [useGradientFill]="true" [animate]="true" [dateUnit]="chartUnit()"
            [xAxis]="{ show: true, showGrid: false, gridStyle: 'dashed', showAxisLine: true, showOnlyFirstLast: false, tickCount: 6 }"
            [yAxis]="{ show: true, showGrid: true, gridStyle: 'dashed', showAxisLine: false, tickCount: 5 }"
            [legend]="{ show: true, position: 'bottom', align: 'center', itemGap: 32 }"
            [tooltip]="{ show: true, showHoverLine: true, valueFormat: 'number' }"
            [margins]="{ top: 20, right: 20, bottom: 40, left: 50 }"></app-line-area-chart>
          }
        </div>
        }

        @case (SectionId.TRANSACTION_AMOUNTS) {
        <div class="relative w-full h-[380px]">
          @if(customRangeStats.loading) {
          <div
            class="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl">
            <app-individual-toaster [loaderType]="'dark'"></app-individual-toaster>
          </div>
          }
          @if(paymentAmountSeries()[0].data.length === 0) {
          <div
            class="w-full h-full bg-white dark:bg-surface-1 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col">
            <h3 class="text-slate-700 dark:text-white font-medium mb-6">Transaction Amounts Over Time</h3>
            <div class="flex-1 flex items-center justify-center text-slate-500 dark:text-slate-400">
              <p class="text-sm">No data available</p>
            </div>
          </div>
          } @else {
          <app-line-area-chart [series]="paymentAmountSeries()" [title]="'Transaction Amounts Over Time'"
            [titleClass]="'text-slate-700 dark:text-white'" [showCard]="true" [cardClass]="'card-bg'"
            [curveType]="'monotoneX'" [useGradientFill]="true" [animate]="true" [dateUnit]="chartUnit()"
            [xAxis]="{ show: true, showGrid: false, gridStyle: 'dashed', showAxisLine: true, showOnlyFirstLast: false, tickCount: 6 }"
            [yAxis]="{ show: true, showGrid: true, gridStyle: 'dashed', showAxisLine: false, tickCount: 5 }"
            [legend]="{ show: true, position: 'bottom', align: 'center', itemGap: 32 }"
            [tooltip]="{ show: true, showHoverLine: true, valueFormat: 'currency' }"
            [margins]="{ top: 20, right: 20, bottom: 40, left: 60 }"></app-line-area-chart>
          }
        </div>
        }

        @case (SectionId.STATUS_DISTRIBUTION) {
        <div
          class="relative w-full h-[380px] bg-white dark:bg-surface-1 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col">
          @if(customRangeStats.loading) {
          <div
            class="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl">
            <app-individual-toaster [loaderType]="'dark'"></app-individual-toaster>
          </div>
          }
          <h3 class="text-slate-700 dark:text-white mb-6">Payment Status Distribution</h3>
          @if(paymentStatusDistribution().length === 0) {
          <div class="flex-1 flex items-center justify-center text-slate-500 dark:text-slate-400">
            <p class="text-sm">No data available</p>
          </div>
          } @else {
          <div class="flex-1 flex-col items-center justify-between gap-8">
            <div class="flex-shrink-0 flex justify-center">
              <div id="paymentStatusPieChart" style="width: 200px; height: 200px;"></div>
            </div>
            <div id="paymentStatusLegend" class="flex flex-col"></div>
          </div>
          }
        </div>
        }

        @case (SectionId.METHOD_DISTRIBUTION) {
        <div
          class="relative w-full h-[380px] bg-white dark:bg-surface-1 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col">
          @if(customRangeStats.loading) {
          <div
            class="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl">
            <app-individual-toaster [loaderType]="'dark'"></app-individual-toaster>
          </div>
          }
          <h3 class="text-slate-700 dark:text-white mb-6">Payment Method Distribution</h3>
          @if(paymentMethodDistribution().length === 0) {
          <div class="flex-1 flex items-center justify-center text-slate-500 dark:text-slate-400">
            <p class="text-sm">No data available</p>
          </div>
          } @else {
          <div class="flex-1 flex-col items-center justify-between gap-8">
            <div class="flex-shrink-0 flex justify-center">
              <div id="paymentMethodPieChart" style="width: 200px; height: 200px;"></div>
            </div>
            <div id="paymentMethodLegend" class="flex flex-col"></div>
          </div>
          }
        </div>
        }

        @case (SectionId.REVENUE_DISTRIBUTION) {
        <div
          class="relative w-full h-[380px] bg-white dark:bg-surface-1 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col">
          @if(customRangeStats.loading) {
          <div
            class="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl">
            <app-individual-toaster [loaderType]="'dark'"></app-individual-toaster>
          </div>
          }
          <h3 class="text-base font-medium text-slate-700 dark:text-white mb-8">Revenue Distribution</h3>
          @if(!customRangeStats.data.revenue_distribution || customRangeStats.data.revenue_distribution.length === 0) {
          <div class="flex-1 flex items-center justify-center text-slate-500 dark:text-slate-400">
            <p class="text-sm">No data available</p>
          </div>
          } @else {
          <div class="flex-1 flex flex-col justify-center gap-8">
            @for(item of customRangeStats.data.revenue_distribution; track item.label) {
            <div class="flex flex-col gap-2">
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-600 dark:text-slate-400">{{ item.label }}</span>
                <span class="text-sm font-semibold text-slate-700 dark:text-white">{{ item.value | currency }}</span>
              </div>
              <div class="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div class="h-full rounded-full transition-all duration-500"
                  [style.width.%]="(item.value / customRangeStats.totalAmount) * 100"
                  [style.background]="getRevenueBarColor(item.label)">
                </div>
              </div>
            </div>
            }
          </div>
          }
        </div>
        }

        @case (SectionId.SUCCESS_RATE_TREND) {
        <div class="relative">
          <div class="absolute right-[8px] top-[12px] z-10">
            <app-toggle-btns [headerOptions]="metricToggleHeaderOptions"
              (viewChanged)="setMetricSelection($event)"></app-toggle-btns>
          </div>
          <div class="relative w-full h-[380px]">
            @if(customRangeStats.loading) {
            <div
              class="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl">
              <app-individual-toaster [loaderType]="'dark'"></app-individual-toaster>
            </div>
            }
            @if(successRateSeries()[0].data.length === 0) {
            <div
              class="w-full h-full bg-white dark:bg-surface-1 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col">
              <h3 class="text-slate-700 dark:text-white font-medium mb-6">Success Rate Trend</h3>
              <div class="flex-1 flex items-center justify-center text-slate-500 dark:text-slate-400">
                <p class="text-sm">No data available</p>
              </div>
            </div>
            } @else {
            @if(chartMetric() === 'rate') {
            <app-line-area-chart [series]="successRateSeries()" [title]="'Success Rate Trend'"
              [titleClass]="'text-slate-700 dark:text-white'" [showCard]="true" [cardClass]="'card-bg'"
              [curveType]="'monotoneX'" [useGradientFill]="false" [animate]="true" [hasRenderedLogic]="false"
              [dateUnit]="chartUnit()"
              [xAxis]="{ show: true, showGrid: false, gridStyle: 'dashed', showAxisLine: true, showOnlyFirstLast: false, tickCount: 6 }"
              [yAxis]="{ show: true, showGrid: true, gridStyle: 'dashed', showAxisLine: false, tickCount: 5 }"
              [legend]="{ show: true, position: 'bottom', align: 'center', itemGap: 32 }"
              [tooltip]="{ show: true, showHoverLine: true, valueFormat: 'percent', showTotal: false }"
              [margins]="{ top: 20, right: 20, bottom: 40, left: 50 }">
            </app-line-area-chart>
            } @else {
            <app-line-area-chart [series]="successAmountSeries()" [title]="'Success Amount Trend'"
              [titleClass]="'text-slate-700 dark:text-white'" [showCard]="true" [cardClass]="'card-bg'"
              [curveType]="'monotoneX'" [useGradientFill]="false" [animate]="true" [hasRenderedLogic]="false"
              [dateUnit]="chartUnit()"
              [xAxis]="{ show: true, showGrid: false, gridStyle: 'dashed', showAxisLine: true, showOnlyFirstLast: false, tickCount: 6 }"
              [yAxis]="{ show: true, showGrid: true, gridStyle: 'dashed', showAxisLine: false, tickCount: 5 }"
              [legend]="{ show: true, position: 'bottom', align: 'center', itemGap: 32 }"
              [tooltip]="{ show: true, showHoverLine: true, valueFormat: 'currency', showTotal: false }"
              [margins]="{ top: 20, right: 20, bottom: 40, left: 50 }">
            </app-line-area-chart>
            }
            }
          </div>
        </div>
        }

        @case (SectionId.DETAILED_STATISTICS) {
        <div class="card-bg rounded-xl p-6 backdrop-blur-sm overflow-hidden">
          <h3 class="text-[#141437] dark:text-white mb-4">Detailed Payment Statistics</h3>
          <div
            [ngClass]="{ 'h-[500px]': paymentStatsTableRows().length > 10, 'h-[300px]': paymentStatsTableRows().length <= 10 }">
            <app-scroll-table [tableData]="paymentStatsTableRows()" [headers]="paymentStatsHeaders"
              [tableKey]="'paymentStats'" [isCheckboxShow]="false" [showHeading]="false"
              [showTableWithBorderAndRadius]="true">
            </app-scroll-table>
          </div>
        </div>
        }

        }
      </div>
      }
    </div>
  </div>
</div>

<!-- custom layout of mini view of the dashboard -->
@if (isLayoutEditorOpen()) {
<div appLayoutEditorOverlay
  class="fixed inset-0 z-[10010] flex items-center justify-center p-3 sm:p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn overflow-hidden"
  (click)="closeLayoutEditor()">

  <div
    class="layout-editor-popup relative w-full max-w-[1100px] max-h-[min(88vh,calc(100vh-2rem))] flex flex-col bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
    (click)="$event.stopPropagation()">

    <!-- Header -->
    <div
      class="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-[#AD46FF]/5 via-white to-[#AD46FF]/5 dark:from-purple-500/10 dark:via-slate-900 dark:to-purple-500/10">
      <div class="flex items-center gap-3 min-w-0">
        <div
          class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#AD46FF] to-[#7c3aed] flex items-center justify-center shadow-lg shadow-[#AD46FF]/30 shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white"
            stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <rect width="7" height="9" x="3" y="3" rx="1" />
            <rect width="7" height="5" x="14" y="3" rx="1" />
            <rect width="7" height="9" x="14" y="12" rx="1" />
            <rect width="7" height="5" x="3" y="16" rx="1" />
          </svg>
        </div>
        <div class="min-w-0">
          <h2 class="text-lg sm:text-xl font-semibold text-[#141437] dark:text-white truncate">
            Customize Layout
          </h2>
          <p class="text-[11px] text-violet-700 dark:text-violet-300">
            <b>Drag</b> a card to reorder &middot; click the <b>width chips</b> to resize &middot; toggle
            the <b>eye</b> to hide/show &middot; the preview mirrors the real dashboard.
          </p>
        </div>
      </div>

      <div class="flex items-center gap-2.5 flex-wrap">
        <button type="button" (click)="autoAdjustEditorWidths()" [disabled]="!canAutoAdjustEditorWidths()"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
          [ngClass]="canAutoAdjustEditorWidths()
                        ? 'bg-white dark:bg-slate-800 text-[#AD46FF] border border-[#AD46FF]/30 hover:bg-violet-50 dark:hover:bg-white/5 hover:border-[#AD46FF]/60 shadow-sm cursor-pointer'
                        : 'bg-slate-50 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700 cursor-not-allowed'"
          title="Expand the smallest card in each row to fill the leftover columns">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
          Auto Adjust Width
        </button>

        <span
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-violet-50 dark:bg-violet-500/10 text-[#AD46FF] border border-[#AD46FF]/20">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          {{ editorVisibleCount() }} / {{ editorItems().length }} visible
        </span>

        <button type="button" (click)="closeLayoutEditor()"
          class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          title="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto custom-scroll bg-[#FAFAFE] dark:bg-slate-950/40 p-3 sm:p-4">

      <div cdkDropList cdkDropListOrientation="mixed" (cdkDropListDropped)="onEditorCardDrop($event)"
        class="grid grid-cols-12 gap-2 sm:gap-2.5 grid-flow-row-dense">

        @for (item of editorItems(); track trackEditorItem($index, item)) {
        <div cdkDrag cdkDragBoundary=".layout-editor-popup" [cdkDragData]="item" [cdkDragDisabled]="!item.visible" [ngClass]="[
                        getEditorColSpanClass(item.colSpan),
                        item.visible
                            ? 'border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-[#AD46FF]/40 cursor-grab active:cursor-grabbing'
                            : 'border-dashed border-slate-300 dark:border-slate-600 opacity-60 cursor-not-allowed'
                    ]"
          class="layout-editor-tile group relative rounded-xl border bg-white dark:bg-slate-800 transition-all duration-200 overflow-hidden">

          <div *cdkDragPlaceholder [ngClass]="getEditorColSpanClass(item.colSpan)"
            class="rounded-xl border-2 border-dashed border-[#AD46FF]/60 bg-violet-50 dark:bg-violet-500/10 min-h-[88px]">
          </div>

          <div class="flex items-start justify-between gap-2 px-2.5 pt-2 pb-1">
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="text-slate-300 dark:text-slate-600 group-hover:text-[#AD46FF] transition-colors shrink-0"
                title="Drag to reorder">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"
                  stroke="none">
                  <circle cx="9" cy="5" r="1" />
                  <circle cx="9" cy="12" r="1" />
                  <circle cx="9" cy="19" r="1" />
                  <circle cx="15" cy="5" r="1" />
                  <circle cx="15" cy="12" r="1" />
                  <circle cx="15" cy="19" r="1" />
                </svg>
              </span>
              <span class="text-[11px] sm:text-[12px] font-semibold text-[#141437] dark:text-white truncate"
                [title]="item.title">
                {{ item.title }}
              </span>
            </div>

            <button type="button" (click)="toggleEditorCardVisibility(item.id); $event.stopPropagation()"
              class="p-1 rounded-md transition-colors shrink-0"
              [ngClass]="item.visible
                                ? 'text-[#AD46FF] hover:bg-violet-50 dark:hover:bg-white/5'
                                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'"
              [title]="item.visible ? 'Hide section' : 'Show section'">
              @if (item.visible) {
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              } @else {
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
              }
            </button>
          </div>

          <div class="px-2.5 pb-2">
            <div
              class="rounded-lg bg-gradient-to-br from-violet-50 via-white to-violet-50/50 dark:from-violet-500/10 dark:via-slate-800 dark:to-violet-500/5 border border-slate-100 dark:border-slate-700/50 h-10 sm:h-10 flex items-center justify-center mb-1.5 overflow-hidden relative">
              <div class="flex items-end gap-1 w-2/3 h-1/2 opacity-70">
                <span class="flex-1 bg-[#AD46FF]/30 rounded-sm" style="height: 40%"></span>
                <span class="flex-1 bg-[#AD46FF]/50 rounded-sm" style="height: 70%"></span>
                <span class="flex-1 bg-[#AD46FF]/40 rounded-sm" style="height: 55%"></span>
                <span class="flex-1 bg-[#AD46FF]/60 rounded-sm" style="height: 90%"></span>
                <span class="flex-1 bg-[#AD46FF]/35 rounded-sm" style="height: 50%"></span>
                <span class="flex-1 bg-[#AD46FF]/55 rounded-sm" style="height: 80%"></span>
              </div>
              <span
                class="absolute top-0.5 right-1 text-[9px] font-semibold text-[#AD46FF]/70 uppercase tracking-wider">
                {{ item.colSpan }}/12
              </span>
            </div>
            <div class="flex items-center gap-1.5">
              <span
                class="text-[10px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wide mr-0.5">Width</span>

              <button type="button" (click)="decreaseEditorCardColSpan(item.id); $event.stopPropagation()"
                [disabled]="!canDecreaseEditorCardColSpan(item)" class="p-1 rounded-md border transition-all"
                [ngClass]="canDecreaseEditorCardColSpan(item)
                                    ? 'bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-[#AD46FF]/50 hover:text-[#AD46FF] hover:bg-violet-50 dark:hover:bg-white/5'
                                    : 'bg-slate-50 dark:bg-slate-800 text-slate-300 dark:text-slate-600 border-slate-100 dark:border-slate-700 cursor-not-allowed'" title="Decrease width">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="14" y1="10" x2="21" y2="3" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>

              <span
                class="inline-flex items-center justify-center min-w-[58px] h-[20px] px-2 rounded-md text-[10px] font-semibold bg-[#AD46FF]/10 text-[#AD46FF] border border-[#AD46FF]/20 select-none whitespace-nowrap">
                {{ getEditorColSpanLabel(item.colSpan) }}
              </span>

              <button type="button" (click)="increaseEditorCardColSpan(item.id); $event.stopPropagation()"
                [disabled]="!canIncreaseEditorCardColSpan(item)" class="p-1 rounded-md border transition-all"
                [ngClass]="canIncreaseEditorCardColSpan(item)
                                    ? 'bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-[#AD46FF]/50 hover:text-[#AD46FF] hover:bg-violet-50 dark:hover:bg-white/5'
                                    : 'bg-slate-50 dark:bg-slate-800 text-slate-300 dark:text-slate-600 border-slate-100 dark:border-slate-700 cursor-not-allowed'" title="Increase width">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        }
      </div>

      @if (editorItems().length === 0) {
      <div class="text-center py-16 text-slate-500 dark:text-slate-400">
        <p class="text-sm">No layout items to customize.</p>
      </div>
      }
    </div>

    <div
      class="flex items-center justify-between gap-3 px-5 sm:px-6 py-3.5 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <button type="button" (click)="resetLayoutEditorToDefaults()"
        class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-[#AD46FF] hover:bg-violet-50 dark:hover:bg-white/5 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </svg>
        Set Default
      </button>

      <div class="flex items-center gap-2">
        <button type="button" (click)="closeLayoutEditor()"
          class="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
          Cancel
        </button>
        <button type="button" (click)="applyLayoutEditor()" [disabled]="editorVisibleCount() === 0"
          class="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#AD46FF] to-[#7c3aed] hover:shadow-lg hover:shadow-[#AD46FF]/30 hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition-all">
          Apply
        </button>
      </div>
    </div>
  </div>
</div>
}